/**
 * Client of the revocable invitation links (M4 item 4, CR-20): the room's staff mints, lists
 * and revokes invitations through POST/GET /api/v1/rooms/{room}/invites and DELETE
 * /api/v1/rooms/{room}/invites/{id}. Every call authenticates with the room-JWT bearer (the
 * join/remint model), so the token comes from the live session, not from the Keycloak session.
 * Same-origin calls: the Vite dev server proxies /api to the control service, production serves
 * the SPA same-origin. Response bodies are narrowed explicitly (unknown -> typed) instead of
 * cast, mirroring src/api/join.ts.
 */

/** Body of POST /api/v1/rooms/{room}/invites. Both fields optional; absent means unlimited. */
export interface CreateInviteBody {
  /** RFC 3339 instant; must be in the future (400 otherwise). Absent = never expires. */
  readonly expiresAt?: string;
  /** Use quota, integer 1..10000. Absent/0 = unlimited. */
  readonly maxUses?: number;
}

/** The 201 response of POST /api/v1/rooms/{room}/invites: the token's ONLY appearance. */
export interface CreatedInvite {
  readonly id: number;
  readonly room: string;
  /** 43-character URL-safe secret; shown exactly once, never listed again. */
  readonly token: string;
  readonly expiresAt: string | null;
  /** 0 = unlimited (the wire omits the field). */
  readonly maxUses: number;
  readonly createdAt: string;
}

/** One management-view row of GET /api/v1/rooms/{room}/invites; never carries the token. */
export interface RoomInvite {
  readonly id: number;
  readonly createdBy: string;
  readonly expiresAt: string | null;
  /** 0 = unlimited (the wire omits the field). */
  readonly maxUses: number;
  readonly usedCount: number;
  readonly revokedAt: string | null;
  readonly createdAt: string;
}

/** Failure of an invites endpoint: HTTP error, network failure or malformed response body. */
export class InvitesApiError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = 'InvitesApiError';
    this.status = status;
    this.code = code;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** Builds the InvitesApiError for a non-2xx response, reading the contract error body when present. */
async function errorFromResponse(response: Response, endpoint: string): Promise<InvitesApiError> {
  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    // Non-JSON error body, keep the generic message.
  }
  let code = 'unknown_error';
  let message = `${endpoint} request failed with HTTP ${response.status}`;
  if (isRecord(body) && isRecord(body.error)) {
    if (typeof body.error.code === 'string') {
      code = body.error.code;
    }
    if (typeof body.error.message === 'string') {
      message = body.error.message;
    }
  }
  return new InvitesApiError(response.status, code, message);
}

async function request(
  room: string,
  path: string,
  init: { method: 'POST' | 'GET' | 'DELETE'; token: string; body?: unknown },
  fetchImpl: typeof fetch,
): Promise<Response> {
  try {
    return await fetchImpl(
      `/api/v1/rooms/${encodeURIComponent(room)}/invites${path}`,
      {
        method: init.method,
        headers: {
          Authorization: `Bearer ${init.token}`,
          ...(init.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
        },
        ...(init.body !== undefined ? { body: JSON.stringify(init.body) } : {}),
      },
    );
  } catch {
    throw new InvitesApiError(0, 'network_error', 'control service is unreachable');
  }
}

/** Narrows one list row; throws TypeError naming the first violating field. */
export function parseRoomInvite(value: unknown): RoomInvite {
  if (!isRecord(value)) {
    throw new TypeError('invite row must be a JSON object');
  }
  const id = value.id;
  if (typeof id !== 'number' || !Number.isInteger(id) || id <= 0) {
    throw new TypeError('invite row field "id" must be a positive integer');
  }
  if (typeof value.createdBy !== 'string') {
    throw new TypeError('invite row field "createdBy" must be a string');
  }
  if (typeof value.usedCount !== 'number' || !Number.isInteger(value.usedCount)) {
    throw new TypeError('invite row field "usedCount" must be an integer');
  }
  if (typeof value.createdAt !== 'string' || value.createdAt.length === 0) {
    throw new TypeError('invite row field "createdAt" must be a timestamp string');
  }
  return {
    id,
    createdBy: value.createdBy,
    expiresAt: typeof value.expiresAt === 'string' ? value.expiresAt : null,
    maxUses: typeof value.maxUses === 'number' ? value.maxUses : 0,
    usedCount: value.usedCount,
    revokedAt: typeof value.revokedAt === 'string' ? value.revokedAt : null,
    createdAt: value.createdAt,
  };
}

/** Narrows the 201 body; the token is required non-empty (its one appearance). */
export function parseCreatedInvite(value: unknown): CreatedInvite {
  if (!isRecord(value)) {
    throw new TypeError('created invite must be a JSON object');
  }
  const id = value.id;
  if (typeof id !== 'number' || !Number.isInteger(id) || id <= 0) {
    throw new TypeError('created invite field "id" must be a positive integer');
  }
  if (typeof value.room !== 'string' || value.room.length === 0) {
    throw new TypeError('created invite field "room" must be a non-empty string');
  }
  if (typeof value.token !== 'string' || value.token.length === 0) {
    throw new TypeError('created invite field "token" must be a non-empty string');
  }
  if (typeof value.createdAt !== 'string' || value.createdAt.length === 0) {
    throw new TypeError('created invite field "createdAt" must be a timestamp string');
  }
  return {
    id,
    room: value.room,
    token: value.token,
    expiresAt: typeof value.expiresAt === 'string' ? value.expiresAt : null,
    maxUses: typeof value.maxUses === 'number' ? value.maxUses : 0,
    createdAt: value.createdAt,
  };
}

/** Mints one invitation; the returned token must be shown to the creating staff member once. */
export async function createInvite(
  room: string,
  token: string,
  body: CreateInviteBody,
  fetchImpl: typeof fetch = globalThis.fetch,
): Promise<CreatedInvite> {
  const wire: Record<string, unknown> = {};
  if (body.expiresAt !== undefined) {
    wire.expiresAt = body.expiresAt;
  }
  if (body.maxUses !== undefined) {
    wire.maxUses = body.maxUses;
  }
  const response = await request(
    room,
    '',
    { method: 'POST', token, body: wire },
    fetchImpl,
  );
  if (response.status !== 201) {
    throw await errorFromResponse(response, 'create invite');
  }
  let payload: unknown;
  try {
    payload = await response.json();
  } catch {
    throw new InvitesApiError(response.status, 'invalid_response', 'create invite response is not JSON');
  }
  try {
    return parseCreatedInvite(payload);
  } catch (cause: unknown) {
    const detail = cause instanceof TypeError ? cause.message : 'malformed create invite response';
    throw new InvitesApiError(response.status, 'invalid_response', detail);
  }
}

/** Lists the room's invitations, newest first, capped at 200 server-side. Never carries tokens. */
export async function listInvites(
  room: string,
  token: string,
  fetchImpl: typeof fetch = globalThis.fetch,
): Promise<RoomInvite[]> {
  const response = await request(room, '', { method: 'GET', token }, fetchImpl);
  if (!response.ok) {
    throw await errorFromResponse(response, 'list invites');
  }
  let payload: unknown;
  try {
    payload = await response.json();
  } catch {
    throw new InvitesApiError(response.status, 'invalid_response', 'list invites response is not JSON');
  }
  if (!isRecord(payload) || !Array.isArray(payload.invites)) {
    throw new InvitesApiError(
      response.status,
      'invalid_response',
      'list invites response field "invites" must be an array',
    );
  }
  try {
    return payload.invites.map(parseRoomInvite);
  } catch (cause: unknown) {
    const detail = cause instanceof TypeError ? cause.message : 'malformed list invites response';
    throw new InvitesApiError(response.status, 'invalid_response', detail);
  }
}

/** Revokes one invitation (204; idempotent on an already-revoked row). Revocation blocks NEW joins only. */
export async function revokeInvite(
  room: string,
  token: string,
  id: number,
  fetchImpl: typeof fetch = globalThis.fetch,
): Promise<void> {
  const response = await request(
    room,
    `/${encodeURIComponent(String(id))}`,
    { method: 'DELETE', token },
    fetchImpl,
  );
  if (response.status !== 204) {
    throw await errorFromResponse(response, 'revoke invite');
  }
}

/** Builds the shareable deep link of a minted invitation. Tokens never appear anywhere else. */
export function inviteLink(origin: string, slug: string, token: string): string {
  return `${origin}/r/${encodeURIComponent(slug)}?invite=${encodeURIComponent(token)}`;
}
